import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { AREAS, CLASSIFICACOES, PESTEL, RECORTES, labelOf } from "@/lib/constants";
import { useTheme } from "@/contexts/ThemeContext";
import SeedDetailModal from "@/components/SeedDetailModal";
import { Maximize2, Minimize2, X, SlidersHorizontal, ChevronLeft, ChevronRight, Layers } from "lucide-react";
import ModuleHelp from "@/components/help/ModuleHelp";

const WORLD_GEO = "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";

const COUNTRY_NAME_MAP = {
  "Brasil": "Brazil",
  "Estados Unidos": "United States of America",
  "Reino Unido": "United Kingdom",
  "Alemanha": "Germany",
  "França": "France",
  "China": "China",
  "Rússia": "Russia",
  "Argentina": "Argentina",
  "Bolívia": "Bolivia",
  "Venezuela": "Venezuela",
  "Colômbia": "Colombia",
  "Peru": "Peru",
  "Paraguai": "Paraguay",
  "Nigéria": "Nigeria",
  "Senegal": "Senegal",
  "África do Sul": "South Africa",
  "Espanha": "Spain",
  "Portugal": "Portugal",
  "Marrocos": "Morocco",
  "União Europeia": null,
};

// Paletas para o modo "Cor por"
const PALETTE_AREA = {
  GLO: "#C5A028", Fronteiras: "#38BDF8", SegurancaMaritima: "#818CF8", InteligenciaDefesa: "#F472B6",
  ApoioInteragencias: "#34D399", DefesaCivil: "#FB7185", GrandesEventos: "#A78BFA",
  Antiterrorismo: "#F59E0B", IlicitosAmbientais: "#10B981",
};
const PALETTE_CLASSIF = {
  TP: "#C5A028", IC: "#FB7185", FPF: "#38BDF8", FPD: "#818CF8", SI: "#F59E0B", WildCard: "#F472B6", EA: "#34D399",
};
const PALETTE_STATUS = {
  aprovado: "#34D399", pendente: "#F59E0B", rejeitado: "#FB7185", rascunho: "#94A3B8", arquivado: "#64748b",
};

const COLOR_MODES = [
  { key: "densidade", label: "Densidade", icon: Layers },
  { key: "area", label: "Cor por Área Temática", icon: Layers },
  { key: "classificacao", label: "Cor por Classificação", icon: Layers },
  { key: "status", label: "Cor por Status", icon: Layers },
];

function dominant(map) {
  let best = null, max = 0;
  for (const k of Object.keys(map || {})) if (map[k] > max) { max = map[k]; best = k; }
  return best;
}

function MapView({ seeds, theme, onCountryClick, colorMode }) {
  const [hover, setHover] = useState(null);
  const isDark = theme === "dark";

  const counts = useMemo(() => {
    const m = {};
    seeds.forEach((s) => {
      const list = [...(s.pais_impacto || [])];
      if (s.source_pais) list.push(s.source_pais);
      list.forEach((c) => {
        const iso = COUNTRY_NAME_MAP[c] || c;
        if (!iso) return;
        if (!m[iso]) m[iso] = { name: iso, total: 0, seeds: [], areas: {}, classes: {}, statuses: {}, topSeed: null };
        m[iso].total += 1;
        m[iso].seeds.push(s);
        if (s.area_tematica_principal) m[iso].areas[s.area_tematica_principal] = (m[iso].areas[s.area_tematica_principal] || 0) + 1;
        if (s.classificacao_semente) m[iso].classes[s.classificacao_semente] = (m[iso].classes[s.classificacao_semente] || 0) + 1;
        if (s.status_validacao) m[iso].statuses[s.status_validacao] = (m[iso].statuses[s.status_validacao] || 0) + 1;
        if (!m[iso].topSeed) m[iso].topSeed = s;
      });
    });
    return m;
  }, [seeds]);

  const maxCount = Math.max(1, ...Object.values(counts).map((c) => c.total));

  const colorFor = (countryData) => {
    if (!countryData || countryData.total === 0) return isDark ? "#0F1A26" : "#e2e8f0";
    if (colorMode === "area") {
      const a = dominant(countryData.areas);
      return PALETTE_AREA[a] || (isDark ? "#3a4a55" : "#94a3b8");
    }
    if (colorMode === "classificacao") {
      const c = dominant(countryData.classes);
      return PALETTE_CLASSIF[c] || (isDark ? "#3a4a55" : "#94a3b8");
    }
    if (colorMode === "status") {
      const s = dominant(countryData.statuses);
      return PALETTE_STATUS[s] || (isDark ? "#3a4a55" : "#94a3b8");
    }
    // densidade
    const t = countryData.total / maxCount;
    if (t < 0.25) return isDark ? "#1f3346" : "#cbd5e1";
    if (t < 0.5) return isDark ? "#3a5a78" : "#94a3b8";
    if (t < 0.75) return "#C5A028";
    return "#D4AF37";
  };

  const bg = isDark ? "#06101A" : "#f8fafc";
  const stroke = isDark ? "#1f3346" : "#cbd5e1";

  return (
    <>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 145, center: [0, 25] }}
        style={{ width: "100%", height: "100%", background: bg }}
      >
        <ZoomableGroup>
          <Geographies geography={WORLD_GEO}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const c = counts[name];
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={colorFor(c)}
                    stroke={stroke}
                    strokeWidth={0.4}
                    style={{
                      default: { outline: "none" },
                      hover: { fill: "#D4AF37", outline: "none", cursor: c?.total ? "pointer" : "default" },
                      pressed: { outline: "none" },
                    }}
                    onMouseEnter={(e) => setHover({ name, c, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setHover((h) => h && { ...h, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => c?.total && onCountryClick?.(c)}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {hover && hover.c?.total && (
        <div
          className="fixed pointer-events-none z-[60] card-mil p-3 text-xs shadow-2xl"
          style={{ left: hover.x + 12, top: hover.y + 12, maxWidth: 320 }}
          data-testid="mapa-tooltip"
        >
          <div className="font-display font-bold text-[var(--text)]">{hover.name}</div>
          <div className="font-mono text-[var(--gold)] mt-1">{hover.c.total} semente{hover.c.total === 1 ? "" : "s"}</div>
          {hover.c.topSeed && (
            <div className="mt-2 pt-2 border-t border-[var(--border)] space-y-0.5">
              <div className="text-[var(--text)] line-clamp-2 italic">{hover.c.topSeed.titulo_insumo}</div>
              <div className="text-[10px] text-[var(--text-muted)] flex flex-wrap gap-2 mt-1">
                <span className="badge badge-gold text-[9px]">{hover.c.topSeed.classificacao_semente || "—"}</span>
                <span>{labelOf(AREAS, hover.c.topSeed.area_tematica_principal)}</span>
                <span className="uppercase">{hover.c.topSeed.status_validacao}</span>
              </div>
            </div>
          )}
          <div className="text-[10px] text-[var(--text-muted)] mt-2 italic">Clique para ver todas as publicações</div>
        </div>
      )}
    </>
  );
}

function FiltersBar({ filters, onChange, onClear, count, totalCount }) {
  const set = (k, v) => onChange({ ...filters, [k]: v || undefined });
  return (
    <div className="card-mil p-3 space-y-2" data-testid="mapa-filters">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mil text-[var(--gold)] flex items-center gap-1.5">
          <SlidersHorizontal size={12} /> Filtros
        </div>
        <div className="text-[10px] font-mono text-[var(--text-muted)]">
          <span className="text-[var(--gold)]">{count}</span> / {totalCount} sementes
          {Object.values(filters).some(Boolean) && (
            <button onClick={onClear} className="ml-3 underline hover:text-[var(--gold)]" data-testid="mapa-filters-clear">limpar</button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
        <select className="input-mil text-xs" value={filters.area || ""} onChange={(e) => set("area", e.target.value)} data-testid="filter-area">
          <option value="">Área (todas)</option>
          {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <select className="input-mil text-xs" value={filters.classif || ""} onChange={(e) => set("classif", e.target.value)} data-testid="filter-classif">
          <option value="">Classificação</option>
          {CLASSIFICACOES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select className="input-mil text-xs" value={filters.pestel || ""} onChange={(e) => set("pestel", e.target.value)} data-testid="filter-pestel">
          <option value="">PESTEL</option>
          {PESTEL.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select className="input-mil text-xs" value={filters.status || ""} onChange={(e) => set("status", e.target.value)} data-testid="filter-status">
          <option value="">Status</option>
          <option value="aprovado">Aprovado</option>
          <option value="pendente">Pendente</option>
          <option value="rejeitado">Rejeitado</option>
          <option value="rascunho">Rascunho</option>
          <option value="arquivado">Arquivado</option>
        </select>
        <select className="input-mil text-xs" value={filters.recorte || ""} onChange={(e) => set("recorte", e.target.value)} data-testid="filter-recorte">
          <option value="">Recorte geográfico</option>
          {RECORTES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select className="input-mil text-xs" value={filters.horizonte || ""} onChange={(e) => set("horizonte", e.target.value)} data-testid="filter-horizonte">
          <option value="">Horizonte</option>
          <option value="2030-2035">2030-2035</option>
          <option value="2035-2040">2035-2040</option>
          <option value="2030-2040">2030-2040</option>
          <option value="2040-2050">2040-2050</option>
          <option value="2050+">2050+</option>
        </select>
        <select className="input-mil text-xs" value={filters.origem || ""} onChange={(e) => set("origem", e.target.value)} data-testid="filter-origem">
          <option value="">Origem</option>
          <option value="manual">Manual</option>
          <option value="upload_ia">Upload + IA</option>
          <option value="automatica">Coleta Automática</option>
          <option value="legada_enriquecida">Legada Enriquecida</option>
          <option value="insumo_v2">Insumo V2</option>
        </select>
      </div>
    </div>
  );
}

function Legend({ colorMode, onChangeMode }) {
  const entries =
    colorMode === "area" ? Object.entries(PALETTE_AREA).map(([k, c]) => ({ key: k, label: labelOf(AREAS, k), color: c })) :
    colorMode === "classificacao" ? Object.entries(PALETTE_CLASSIF).map(([k, c]) => ({ key: k, label: labelOf(CLASSIFICACOES, k), color: c })) :
    colorMode === "status" ? Object.entries(PALETTE_STATUS).map(([k, c]) => ({ key: k, label: k, color: c })) :
    [];
  return (
    <div className="card-mil px-3 py-2 text-xs space-y-1.5" data-testid="mapa-legend">
      <select className="input-mil text-[10px] w-full" value={colorMode} onChange={(e) => onChangeMode(e.target.value)} data-testid="mapa-color-mode">
        {COLOR_MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
      </select>
      {colorMode === "densidade" ? (
        <div className="flex items-center gap-1">
          <span className="text-[9px] text-[var(--text-muted)]">menor</span>
          {[0, 0.3, 0.55, 0.8, 1].map((t) => {
            const swatch = t === 0 ? "#cbd5e1" : t < 0.5 ? "#94a3b8" : t < 0.8 ? "#C5A028" : "#D4AF37";
            return <div key={`swatch-${t}`} className="w-4 h-3 rounded" style={{ background: swatch }} />;
          })}
          <span className="text-[9px] text-[var(--text-muted)]">maior</span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1 max-w-[280px]">
          {entries.map((e) => (
            <div key={e.key} className="flex items-center gap-1 text-[10px]">
              <div className="w-2.5 h-2.5 rounded" style={{ background: e.color }} />
              <span className="capitalize">{e.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function applyFilters(seeds, filters) {
  return seeds.filter((s) => {
    if (filters.area && s.area_tematica_principal !== filters.area) return false;
    if (filters.classif && s.classificacao_semente !== filters.classif) return false;
    if (filters.pestel && !(s.perspectiva_pestel || []).includes(filters.pestel)) return false;
    if (filters.status && s.status_validacao !== filters.status) return false;
    if (filters.recorte && s.recorte_geografico !== filters.recorte) return false;
    if (filters.horizonte && s.horizonte_temporal !== filters.horizonte) return false;
    if (filters.origem && s.origem_cadastro !== filters.origem) return false;
    return true;
  });
}

export default function Mapa() {
  const [seeds, setSeeds] = useState([]);
  const [openSeed, setOpenSeed] = useState(null);
  const [countryDrawer, setCountryDrawer] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showRanking, setShowRanking] = useState(true);
  const [filters, setFilters] = useState({});
  const [colorMode, setColorMode] = useState("densidade");
  const { theme } = useTheme();

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/seeds", { params: { limit: 500 } })
        .then((r) => setSeeds(r.data))
        .catch(() => { /* silently ignore — sem sementes ainda */ });
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const filtered = useMemo(() => applyFilters(seeds, filters), [seeds, filters]);

  const counts = useMemo(() => {
    const m = {};
    filtered.forEach((s) => {
      const list = [...(s.pais_impacto || [])];
      if (s.source_pais) list.push(s.source_pais);
      list.forEach((c) => {
        const iso = COUNTRY_NAME_MAP[c] || c;
        if (!iso) return;
        if (!m[iso]) m[iso] = { name: iso, total: 0, areas: {}, seeds: [] };
        m[iso].total += 1;
        m[iso].areas[s.area_tematica_principal] = (m[iso].areas[s.area_tematica_principal] || 0) + 1;
        m[iso].seeds.push(s);
      });
    });
    return m;
  }, [filtered]);

  const topCountries = Object.values(counts).sort((a, b) => b.total - a.total).slice(0, 12);

  const mapHeight = "calc(100vh - 220px)";

  return (
    <div className="space-y-3" data-testid="mapa-page">
      {/* Cabeçalho compacto */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mil text-[var(--gold)]">Cartografia Estratégica</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold mt-1 flex items-center gap-3">Mapa Geográfico <ModuleHelp module="mapa" /></h1>
          <p className="text-[var(--text-muted)] text-xs mt-1">
            {filtered.length} de {seeds.length} sementes plotadas — clique em um país para ver as publicações.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`btn-ghost text-xs flex items-center gap-1.5 ${showFilters ? "border-[var(--gold)] text-[var(--gold)]" : ""}`}
            data-testid="mapa-toggle-filters"
          >
            <SlidersHorizontal size={12} /> {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
            {Object.values(filters).filter(Boolean).length > 0 && (
              <span className="badge badge-gold text-[9px] ml-1">{Object.values(filters).filter(Boolean).length}</span>
            )}
          </button>
          <button
            onClick={() => setShowRanking((v) => !v)}
            className="btn-ghost text-xs flex items-center gap-1.5"
            data-testid="mapa-toggle-ranking"
          >
            {showRanking ? <ChevronRight size={12} /> : <ChevronLeft size={12} />} {showRanking ? "Ocultar ranking" : "Mostrar ranking"}
          </button>
          <button
            onClick={() => setFullscreen(true)}
            className="btn-gold text-xs flex items-center gap-1.5"
            data-testid="mapa-expand"
          >
            <Maximize2 size={12} /> Expandir mapa
          </button>
        </div>
      </div>

      {showFilters && (
        <FiltersBar filters={filters} onChange={setFilters} onClear={() => setFilters({})} count={filtered.length} totalCount={seeds.length} />
      )}

      {/* Layout principal — mapa dominante */}
      <div className={`grid grid-cols-1 ${showRanking ? "xl:grid-cols-5" : ""} gap-3`}>
        <div
          className={`card-mil p-0 overflow-hidden relative ${showRanking ? "xl:col-span-4" : ""}`}
          style={{ height: mapHeight, minHeight: 480 }}
          data-testid="mapa-world"
        >
          <MapView seeds={filtered} theme={theme} onCountryClick={setCountryDrawer} colorMode={colorMode} />
          <div className="absolute bottom-3 left-3 z-10">
            <Legend colorMode={colorMode} onChangeMode={setColorMode} />
          </div>
        </div>

        {showRanking && (
          <div className="card-mil p-4 gold-accent-top overflow-y-auto scrollbar-thin" style={{ maxHeight: mapHeight }} data-testid="mapa-ranking">
            <div className="flex items-center justify-between mb-3">
              <div className="label-mil">Top Países</div>
              <span className="text-[10px] font-mono text-[var(--text-muted)]">{topCountries.length}</span>
            </div>
            <ul className="space-y-2">
              {topCountries.length === 0 && (
                <li className="text-xs text-[var(--text-muted)] italic">Nenhum país com sementes para os filtros atuais.</li>
              )}
              {topCountries.map((c) => (
                <li key={c.name} className="text-sm">
                  <button
                    onClick={() => setCountryDrawer(c)}
                    className="w-full text-left hover:bg-[var(--surface-2)] rounded p-1 -m-1"
                    data-testid={`country-row-${c.name}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{c.name}</span>
                      <span className="badge badge-gold">{c.total}</span>
                    </div>
                    <div className="text-[10px] font-mono text-[var(--text-muted)] mt-0.5 truncate">
                      {Object.entries(c.areas).slice(0, 3).map(([a, n]) => `${labelOf(AREAS, a)} (${n})`).join(" · ")}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-50 bg-[var(--bg)] flex flex-col" data-testid="mapa-fullscreen">
          <div className="h-12 border-b border-[var(--border)] flex items-center justify-between px-4 bg-[var(--surface)]">
            <div className="font-display font-bold text-[var(--text)] flex items-center gap-2">
              Mapa Geográfico · Tela Cheia
              <span className="text-xs font-normal text-[var(--text-muted)]">{filtered.length} sementes</span>
            </div>
            <div className="flex items-center gap-2">
              <Legend colorMode={colorMode} onChangeMode={setColorMode} />
              <button
                onClick={() => setFullscreen(false)}
                className="btn-ghost text-xs flex items-center gap-1.5"
                data-testid="mapa-collapse"
              >
                <Minimize2 size={14} /> Fechar tela cheia
              </button>
            </div>
          </div>
          <div className="flex-1 relative">
            <MapView seeds={filtered} theme={theme} onCountryClick={setCountryDrawer} colorMode={colorMode} />
          </div>
        </div>
      )}

      {/* Drawer com sementes do país clicado */}
      {countryDrawer && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
          onClick={() => setCountryDrawer(null)}
          data-testid="country-drawer-overlay"
        >
          <div
            className="w-full max-w-md h-full card-mil rounded-none border-l border-[var(--border)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="country-drawer"
          >
            <div className="p-4 border-b border-[var(--border)] flex items-center justify-between gap-3">
              <div>
                <div className="label-mil text-[var(--gold)]">País selecionado</div>
                <h3 className="font-display font-bold text-lg text-[var(--text)] mt-0.5">{countryDrawer.name}</h3>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">{countryDrawer.total} sementes associadas</p>
              </div>
              <button onClick={() => setCountryDrawer(null)} className="p-2 hover:bg-[var(--surface-2)] rounded text-[var(--text-muted)]" data-testid="drawer-close">
                <X size={16} />
              </button>
            </div>
            <ul className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-[var(--border)]">
              {countryDrawer.seeds.map((s) => (
                <li key={s.seed_id}>
                  <button
                    onClick={() => setOpenSeed(s.seed_id)}
                    className="w-full text-left p-4 hover:bg-[var(--surface-2)]"
                    data-testid={`drawer-seed-${s.seed_id}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="badge badge-gold">{s.classificacao_semente}</span>
                      <span className="text-[10px] font-mono text-[var(--text-muted)]">{labelOf(AREAS, s.area_tematica_principal)}</span>
                    </div>
                    <div className="font-semibold text-sm text-[var(--text)] line-clamp-2">{s.titulo_insumo}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-1 truncate">{s.source_titulo}</div>
                    <div className="flex items-center justify-between mt-2 text-[10px] font-mono text-[var(--text-muted)]">
                      <span>{s.horizonte_temporal}</span>
                      <span>{s.created_by_user_name || "—"}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <SeedDetailModal seedId={openSeed} onClose={() => setOpenSeed(null)} onChanged={() => {}} />
    </div>
  );
}
